import React, { useState } from 'react';
import mixpanel from 'mixpanel-browser';
import { useSelector, useDispatch } from 'react-redux';

import { Button, FormControl, Input } from '@mui/material';
import { DirectionsCar as DirectionsCarIcon, Home as HomeIcon } from '@mui/icons-material';

import { AutoFinancingSection } from './AutoFinancingSection/AutoFinancingSection.js';
import { HomeFinancingSection } from './HomeFinancingSection/HomeFinancingSection.js';
import { MortgagesDropdown } from './MortgagesDropdown.js';
import { LoanTermTabs } from './LoanTermTabs.js';
import { CreditTierTabs } from './CreditTierTabs.js';
import { EstimateValueInput } from './EstimateValueInput.js';

import { checkUserFilterRates } from '../../actions/index.js';

import {
  FINANCING,
  AUTO,
  HOME,
  LOAN_TYPE,
  SELECTED,
  NOT_SELECTED,
  DOWN_PAYMENT,
  TRADE_IN_VALUE,
  RESET_ALL,
  CHECK_RATES,
  state_rates_filter,
  zip_code_rates_filter,
} from '../../constants/Dashboard';

import {
  MAKE,
  MODEL,
  YEAR,
  COLOR,
  AUTO_TYPE,
  MILEAGE,
  LOAN_AMOUNT,
  PROPERTY_TYPE,
  OCCUPANCY_TYPE,
} from '../../constants/ComparisonSummary';

import {
  FORM_CONTROL_FULL_WIDTH_INPUT_STYLE,
  CREDIT_SCORE_OBJ,
  GOOD,
  DEFAULT_LOAN_TERM,
} from '../../constants/Dashboard/Financing';

import { DEFAULT_MORTGAGE } from '../../constants/Dashboard/Financing/mortgage';

import './FinancingSection.css';

/*
  TO DO - We have to think about controlling the convention and pattern
  in regards to the re-rendering and controlling of the make, models, auto types
  and year of the vehicles
*/

export const FinancingSection = ({
  us_state,
  zip_code,
  houseOfWorshipZipCode,
  houseOfWorshipState,
  employmentUsState,
  employmentZipCode,
  user,
  setPathname,
  segsList,
  isQuorumEligible,
}) => {
  const { financeTypeFromSelector, residencyReducer, ...vehOrPropReducer } = useSelector(
    ({ residencyReducer, vehicleReducer, propertyReducer }) => ({
      residencyReducer,
      [AUTO]: {
        [AUTO_TYPE]: vehicleReducer.auto_type,
        [COLOR]: vehicleReducer.color,
        [DOWN_PAYMENT]: vehicleReducer.downPayment,
        estimateValue: vehicleReducer.estimateValue,
        [MAKE]: vehicleReducer.make,
        [MILEAGE]: vehicleReducer.mileage,
        [MODEL]: vehicleReducer.model,
        [TRADE_IN_VALUE]: vehicleReducer.tradeInValue,
        [YEAR]: vehicleReducer.year,
        [LOAN_AMOUNT]:
          Number(vehicleReducer.estimateValue) -
          (Number(vehicleReducer.downPayment) + Number(vehicleReducer.tradeInValue)),
        selectedCreditTier: vehicleReducer.selectedCreditTier,
        loanTermMonths: vehicleReducer.selectedLoanTerms,
      },
      [HOME]: {
        [DOWN_PAYMENT]: propertyReducer.downPayment,
        estimateValue: propertyReducer.estimateValue,
        [TRADE_IN_VALUE]: propertyReducer.tradeInValue,
        [PROPERTY_TYPE]: propertyReducer.propertyType,
        [OCCUPANCY_TYPE]: propertyReducer.occupancyType,
        [LOAN_AMOUNT]:
          Number(propertyReducer.estimateValue) -
          (Number(propertyReducer.downPayment) + Number(propertyReducer.tradeInValue)),
        selectedCreditTier: propertyReducer.selectedCreditTier,
        mortgageTermInYears: propertyReducer?.mortgageTermInYears,
      },
      financeTypeFromSelector: vehicleReducer.financingType || propertyReducer.financingType,
    }),
  );

  const dispatch = useDispatch();
  const [financingType, setFinancingType] = useState(financeTypeFromSelector || AUTO);
  const [selectedLoanTerms, setSelectedLoanTerms] = useState(
    vehOrPropReducer?.[financingType]?.['loanTermMonths'] || { 60: 60 },
  );
  const [selectedCreditTier, setSelectedCreditTier] = useState(
    vehOrPropReducer[financingType].selectedCreditTier || GOOD,
  );
  const defaultMortgageTerm = Object.values(
    vehOrPropReducer?.[financingType]?.['mortgageTermInYears'] || {},
  ).length
    ? Object.values(vehOrPropReducer?.[financingType]?.['mortgageTermInYears'])
    : DEFAULT_MORTGAGE;
  const [mortgageTerm, setMortgageTerm] = useState(defaultMortgageTerm);
  const [downPayment, setDownPayment] = useState(vehOrPropReducer[financingType][DOWN_PAYMENT]);
  const [tradeInValue, setTradeInValue] = useState(vehOrPropReducer[financingType][TRADE_IN_VALUE]);
  const [estimateValue, setEstimateValue] = useState(vehOrPropReducer[financingType].estimateValue);

  const [resetValues, setResetValues] = useState(false);
  const [checkRates, setCheckRates] = useState(false);
  const [vehicleYear, setVehicleYear] = useState('');

  const handleReset = () => {
    setResetValues(() => {
      setTimeout(() => {
        setResetValues(false);
      }, 1000);

      return true;
    });

    mixpanel.track(`Reset Dashboard Filter - ${financingType}`, {
      user_id: user.id,
      financingType,
      distinct_id: user.id,
    });

    setDownPayment('');
    setTradeInValue('');
    setSelectedLoanTerms(DEFAULT_LOAN_TERM);
    setSelectedCreditTier(GOOD);
    setMortgageTerm(DEFAULT_MORTGAGE);
    setEstimateValue('');
  };

  const isAutoFinancing = financingType === AUTO;
  const isHomeFinancing = financingType === HOME;

  const handleCheckRates = () => {
    const loan_amount =
      (Number(estimateValue) || 0) - (Number(downPayment) || 0) - (Number(tradeInValue) || 0);

    setCheckRates(true);

    setTimeout(() => {
      setCheckRates(false);
      setPathname('/rates');
    }, 300);

    dispatch(
      checkUserFilterRates({
        body: {
          [state_rates_filter]: us_state,
          [zip_code_rates_filter]: zip_code,
          houseOfWorshipZipCode,
          houseOfWorshipState,
          employmentUsState,
          employmentZipCode,
          loan_amount,
          loanAmount: loan_amount,
          credit_score: CREDIT_SCORE_OBJ[selectedCreditTier],
          creditTier: selectedCreditTier,
          loan_type: LOAN_TYPE[financingType],
          term_months: isAutoFinancing
            ? Object.values(selectedLoanTerms)
            : mortgageTerm.map((m) => Number(m) * 12),
          loanTerms: Object.values(selectedLoanTerms),
          mortgageLoanTermMonth: mortgageTerm.map((m) => Number(m) * 12),
          vehicleYear,
          vehicle_year: vehicleYear,
          down_payment: downPayment,
          user,
          propertyType: vehOrPropReducer[financingType]?.['Property Type'],
          occupancyType: vehOrPropReducer[financingType]?.['Occupancy'],
          make: vehOrPropReducer[financingType]?.['Make'],
          model: vehOrPropReducer[financingType]?.['Model'],
          mileage: vehOrPropReducer[financingType]?.['Mileage'],
          auto_type: vehOrPropReducer[financingType]?.['Auto Type'],
          segsList,
          isQuorumEligible,
          estimateValue,
        },
        financingInfo: {
          financingType,
          selectedLoanTerms,
          selectedCreditTier,
          mortgageTerm: mortgageTerm.reduce(
            (acc, curr) => ({ ...acc, [Number(curr) * 12]: curr }),
            {},
          ),
          mortgageTermInYears: mortgageTerm.reduce((acc, curr) => ({ ...acc, [curr]: curr }), {}),
          downPayment,
          tradeInValue,
          estimateValue,
          year: vehicleYear,
          vehicleYear,
        },
      }),
    );
    mixpanel.track(`Check Rates Dashboard ${financingType}`, {
      user_id: user.id,
      financingType,
      [state_rates_filter]: us_state,
      [zip_code_rates_filter]: zip_code,
      houseOfWorshipZipCode,
      houseOfWorshipState,
      employmentUsState,
      employmentZipCode,
      loanAmount: loan_amount,
      credit_score: CREDIT_SCORE_OBJ[selectedCreditTier],
      loan_type: `${LOAN_TYPE[financingType]}`,
      term_months: isAutoFinancing
        ? Object.values(selectedLoanTerms)
        : mortgageTerm.map((m) => Number(m) * 12),
      vehicle_year: vehicleYear,
      mortgageLoanTermMonth: mortgageTerm.map((m) => Number(m) * 12),
      vehicleYear,
      down_payment: downPayment,
      user,
      propertyType: vehOrPropReducer[financingType]?.['Property Type'],
      occupancyType: vehOrPropReducer[financingType]?.['Occupancy'],
      make: vehOrPropReducer[financingType]?.['Make'],
      model: vehOrPropReducer[financingType]?.['Model'],
      mileage: vehOrPropReducer[financingType]?.['Mileage'],
      auto_type: vehOrPropReducer[financingType]?.['Auto Type'],
      segsList,
      isQuorumEligible,
      estimateValue,
    });
  };

  const handleLoanTermClick = (loanTerm) => {
    setSelectedLoanTerms((prevState) => {
      if (prevState[loanTerm]) {
        const copyState = { ...prevState };
        delete copyState[loanTerm];

        mixpanel.track(`Finance Section ${financingType}`, {
          user_id: user.id,
          financingType,
          FINANCE_FIELD: 'Loan Terms',
          LOAN_TERMS: copyState,
          distinct_id: user.id,
        });

        return { ...copyState };
      }

      const updatedLoanTerms = { ...prevState, [loanTerm]: Number(loanTerm) };

      mixpanel.track(`Finance Section ${financingType}`, {
        user_id: user.id,
        financingType,
        FINANCE_FIELD: 'Loan Terms',
        LOAN_TERMS: updatedLoanTerms,
      });

      return updatedLoanTerms;
    });
  };

  const handleMortgageTermsChange = (mortgageTerm) => {
    setMortgageTerm(mortgageTerm);
    mixpanel.track(`Finance Section ${financingType}`, {
      user_id: user.id,
      financingType,
      FINANCE_FIELD: 'MORTGAGE TERMS',
      MORTGAGE_TERMS: mortgageTerm,
    });
  };

  const handleFinancingTab = (FinancingType) => {
    setFinancingType(FinancingType);
    mixpanel.track(`FINANCE TYPE - ${financingType}`, {
      user_id: user.id,
      finance: FinancingType,
      distinct_id: user.id,
    });
  };

  const handleSelectCreditTier = (creditTier) => {
    setSelectedCreditTier(creditTier);
    mixpanel.track(`Finance Section ${financingType}`, {
      user_id: user.id,
      financingType,
      FINANCE_FIELD: 'CREDIT TIER',
      CREDIT_TIER: creditTier,
      distinct_id: user.id,
    });
  };

  const handleEstimateValueChange = (estimateValue) => {
    setEstimateValue(estimateValue);
    mixpanel.track(`Finance Section ${financingType}`, {
      user_id: user.id,
      financingType,
      FINANCE_FIELD: 'ESTIMATE VALUE',
      ESTIMATE_VALUE: estimateValue,
      distinct_id: user.id,
    });
  };

  const autoButtonStyle = isAutoFinancing ? SELECTED : NOT_SELECTED;
  const homeButtonStyle = isHomeFinancing ? SELECTED : NOT_SELECTED;

  return (
    <div>
      <div className="financing-section-header">
        {' '}
        <h4 className="financing-header-title">{FINANCING}</h4>
        <div>
          <Button id={autoButtonStyle} onClick={() => handleFinancingTab(AUTO)}>
            <DirectionsCarIcon className="financing-row-icons" />
            <div className="financing-button-text">{AUTO}</div>
          </Button>
          <Button id={homeButtonStyle} onClick={() => handleFinancingTab(HOME)}>
            <HomeIcon className="financing-row-icons" />
            <div className="financing-button-text">{HOME}</div>
          </Button>
        </div>
      </div>
      {isAutoFinancing && (
        <AutoFinancingSection
          resetValues={resetValues}
          checkRates={checkRates}
          setVehicleYear={setVehicleYear}
          user={user}
        />
      )}
      {isHomeFinancing && (
        <HomeFinancingSection resetValues={resetValues} checkRates={checkRates} user={user} />
      )}

      <div className="downpayment-trade-in-row">
        <div className="financing-field-wrapper downpayment">
          <p className="auto-type-mileage-label">{DOWN_PAYMENT}</p>
          <FormControl sx={FORM_CONTROL_FULL_WIDTH_INPUT_STYLE}>
            <Input
              id="financing-select-helper"
              value={downPayment}
              autoComplete="off"
              onChange={(e) => {
                if (!isNaN(e.target.value)) {
                  setDownPayment(e.target.value);
                  mixpanel.track(`Finance Section ${financingType}`, {
                    financingType,
                    FINANCE_FIELD: 'Down Payment',
                    DOWN_PAYMENT: e.target.value,
                    user_id: user.id,
                  });
                } else {
                  setDownPayment('');
                }
              }}
              disableUnderline
            />
          </FormControl>
        </div>
        <div className="financing-field-wrapper trade-in">
          <p className="auto-type-mileage-label">{TRADE_IN_VALUE}</p>
          <FormControl sx={FORM_CONTROL_FULL_WIDTH_INPUT_STYLE}>
            <Input
              id="financing-select-helper"
              value={tradeInValue}
              autoComplete="off"
              onChange={(e) => {
                if (!isNaN(e.target.value)) {
                  setTradeInValue(e.target.value);
                  mixpanel.track(`Finance Section ${financingType}`, {
                    financingType,
                    FINANCE_FIELD: 'Trade In Value',
                    TRADE_IN_VALUE: e.target.value,
                    user_id: user.id,
                  });
                } else {
                  setTradeInValue('');
                }
              }}
              disableUnderline
            />
          </FormControl>
        </div>
      </div>

      <div className="loan-term-credit-tier-wrapper">
        {isAutoFinancing && (
          <LoanTermTabs
            handleLoanTermClick={handleLoanTermClick}
            selectedLoanTerms={selectedLoanTerms}
          />
        )}
        {isHomeFinancing && (
          <MortgagesDropdown
            mortgageTerm={mortgageTerm}
            handleMortgageTermsChange={handleMortgageTermsChange}
          />
        )}
        <CreditTierTabs
          selectedCreditTier={selectedCreditTier}
          handleSelectCreditTier={handleSelectCreditTier}
        />
      </div>
      <EstimateValueInput
        handleEstimateValueChange={handleEstimateValueChange}
        estimateValue={estimateValue}
      />

      <div className="financing-buttons-section-wrapper">
        <Button
          sx={{
            '&:hover': { backgroundColor: 'rgb(240, 240, 240);', color: 'black' },
            m: 0,
            color: 'black',
            textTransform: 'initial',
            backgroundColor: 'rgb(240, 240, 240)',
            width: '97.5%',
            borderRadius: '10px',
            marginRight: 1,
            height: '48px',
            marginBottom: 2,
            maxWidth: 410,
          }}
          onClick={handleReset}
        >
          {RESET_ALL}
        </Button>
        <Button
          sx={{
            '&:hover': { backgroundColor: 'black', color: 'white' },
            '&:disabled': { backgroundColor: 'gray', color: 'black', opacity: 0.7 },
            m: 0,
            color: 'white',
            textTransform: 'initial',
            backgroundColor: 'black',
            width: '97.5%',
            maxWidth: 410,
            borderRadius: '10px',
            marginRight: '10px',
            height: '48px',
            marginBottom: '20px',
          }}
          disabled={checkRates}
          onClick={handleCheckRates}
        >
          {CHECK_RATES}
        </Button>
      </div>
    </div>
  );
};
